import { useMemo } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'

import type { DeliveryTripStatus } from '@/lib/api/delivery-trip-api'
import { PageBackButton } from '@/components/common/PageBackButton'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { defineListFilters } from '@/components/common/filters/core'
import { dateFilter } from '@/components/common/filters/date-filter'
import { selectFilter } from '@/components/common/filters/select-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { LIST_CARD_TITLE_CLASS, ListCard } from '@/components/common/ListCard'
import { useDeliveryTrips } from '@/hooks/use-delivery-trips-query'
import { useAuth } from '@/hooks/use-auth'
import { useDashboardRole } from '@/components/dashboard/use-dashboard-role'
import { driverTripScope } from '@/lib/delivery-trip-scope'
import { m } from '@/lib/paraglide/messages'

const statusLabel = (s: DeliveryTripStatus) =>
    s === 'active' ? m.trip_status_active() : m.trip_status_closed()

/**
 * Bộ lọc khai một lần, suy ra: parser URL, mặc định, badge đếm, chip.
 *
 * Trước đây cả ba nằm trong `useState` nên không vào URL, và hai ô ngày còn
 * **không có nhãn** — nhìn hai ô `dd/mm/yyyy` cạnh nhau không biết đâu là "từ"
 * đâu là "đến" (R7).
 */
const FILTERS = defineListFilters({
    status: selectFilter<DeliveryTripStatus | 'all'>({
        label: m.trip_status_filter_label,
        defaultValue: 'all',
        options: () => [
            { value: 'all', label: m.vehicle_status_all() },
            { value: 'active', label: m.trip_status_active() },
            { value: 'closed', label: m.trip_status_closed() },
        ],
    }),
    from: dateFilter({ label: m.report_from_date }),
    to: dateFilter({ label: m.report_to_date }),
})

export const Route = createFileRoute('/_app/_authed/_sidebar/delivery-trips/')({
    component: DeliveryTripsPage,
    validateSearch: FILTERS.validateSearch,
})

function DeliveryTripsPage() {
    const navigate = useNavigate({ from: '/delivery-trips/' })
    const { values, barProps, page, setPage } = useListFilters({
        def: FILTERS,
        search: Route.useSearch(),
        onSearchChange: (next, opts) =>
            void navigate({
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        columns: 4,
    })

    // A driver only sees their own trips; admin/manager see all. Scope is
    // applied via the driver_id filter (UI-level — see driverTripScope).
    const { user } = useAuth()
    const dashboardRole = useDashboardRole({ allowKioskRedirect: false })
    const isDriver =
        dashboardRole.kind === 'dashboard' &&
        dashboardRole.dashboard === 'driver'
    const driverId = driverTripScope(isDriver, user?.id)

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu.
    const params = useMemo(
        () => ({
            page,
            per_page: 20,
            status: values.status === 'all' ? undefined : values.status,
            from: values.from || undefined,
            to: values.to || undefined,
            driver_id: driverId,
        }),
        [page, values.status, values.from, values.to, driverId],
    )

    const { data, isLoading } = useDeliveryTrips(params)
    const trips = data?.data ?? []
    const meta = data?.meta

    return (
        <div className="space-y-4 page-pad">
            <Card>
                <CardHeader>
                    <div className="flex min-w-0 items-center gap-2">
                        <PageBackButton className="-my-2 md:-my-1" />
                        <CardTitle>{m.trip_history_title()}</CardTitle>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <ListFilterBar {...barProps} />

                    {isLoading ? (
                        <p className="text-sm text-muted-foreground">
                            {m.common_loading()}
                        </p>
                    ) : trips.length === 0 ? (
                        // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá
                        // hẹp — trường hợp thứ hai phải cho lối thoát (R10).
                        <div className="flex flex-col items-center gap-3 py-8 text-center">
                            <span className="text-sm text-muted-foreground">
                                {barProps.activeCount > 0
                                    ? m.list_empty_filtered()
                                    : m.trip_empty()}
                            </span>
                            {barProps.activeCount > 0 ? (
                                <ClearFilterButton
                                    active
                                    onClear={barProps.onClear}
                                />
                            ) : null}
                        </div>
                    ) : (
                        <>
                            {/* Mobile: card list */}
                            <div className="space-y-2 md:hidden">
                                {trips.map((t) => (
                                    <ListCard
                                        key={t.id}
                                        title={
                                            <Link
                                                to="/delivery-trips/$id"
                                                params={{ id: t.id }}
                                                className={
                                                    LIST_CARD_TITLE_CLASS
                                                }
                                            >
                                                {t.trip_code}
                                            </Link>
                                        }
                                        subtitle={t.driver_name ?? '—'}
                                        badge={
                                            <Badge
                                                variant={
                                                    t.status === 'active'
                                                        ? 'success'
                                                        : 'secondary'
                                                }
                                            >
                                                {statusLabel(t.status)}
                                            </Badge>
                                        }
                                        fields={[
                                            {
                                                label: m.trip_vehicle(),
                                                value: t.vehicle
                                                    ? `${t.vehicle.plate_number} — ${t.vehicle.name}`
                                                    : '—',
                                                full: true,
                                            },
                                            {
                                                label: m.trip_started_at(),
                                                value: new Date(
                                                    t.started_at,
                                                ).toLocaleString(),
                                            },
                                            {
                                                label: m.trip_ended_at(),
                                                value: t.ended_at
                                                    ? new Date(
                                                          t.ended_at,
                                                      ).toLocaleString()
                                                    : m.trip_running_now(),
                                            },
                                        ]}
                                    />
                                ))}
                            </div>

                            {/* Desktop: table */}
                            <div className="hidden md:block overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>
                                                {m.trip_code()}
                                            </TableHead>
                                            <TableHead>
                                                {m.trip_driver()}
                                            </TableHead>
                                            <TableHead>
                                                {m.trip_vehicle()}
                                            </TableHead>
                                            <TableHead>
                                                {m.common_status_label()}
                                            </TableHead>
                                            <TableHead>
                                                {m.trip_started_at()}
                                            </TableHead>
                                            <TableHead>
                                                {m.trip_ended_at()}
                                            </TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {trips.map((t) => (
                                            <TableRow key={t.id}>
                                                <TableCell className="font-medium">
                                                    <Link
                                                        to="/delivery-trips/$id"
                                                        params={{ id: t.id }}
                                                        className="text-primary hover:underline"
                                                    >
                                                        {t.trip_code}
                                                    </Link>
                                                </TableCell>
                                                <TableCell>
                                                    {t.driver_name ?? '—'}
                                                </TableCell>
                                                <TableCell>
                                                    {t.vehicle
                                                        ? `${t.vehicle.plate_number} — ${t.vehicle.name}`
                                                        : '—'}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge
                                                        variant={
                                                            t.status ===
                                                            'active'
                                                                ? 'success'
                                                                : 'secondary'
                                                        }
                                                    >
                                                        {statusLabel(t.status)}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-sm text-muted-foreground">
                                                    {new Date(
                                                        t.started_at,
                                                    ).toLocaleString()}
                                                </TableCell>
                                                <TableCell className="text-sm text-muted-foreground">
                                                    {t.ended_at
                                                        ? new Date(
                                                              t.ended_at,
                                                          ).toLocaleString()
                                                        : m.trip_running_now()}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </>
                    )}

                    {meta && meta.total_pages > 1 && (
                        <div className="flex items-center justify-end gap-2 pt-2">
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={page <= 1}
                                onClick={() => setPage(page - 1)}
                            >
                                {m.common_prev()}
                            </Button>
                            <span className="text-sm text-muted-foreground">
                                {page} / {meta.total_pages}
                            </span>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={page >= meta.total_pages}
                                onClick={() => setPage(page + 1)}
                            >
                                {m.common_next()}
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}
